"use client";

import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { extractApiErrorMessage } from "../../../utils/error-handler";
import { fetchPlans, recordManualPayment } from "../../../services/billingService";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatPaise } from "../../../utils/billingFormat";

const PAYMENT_METHODS = ["UPI", "CARD", "NETBANKING", "BANK_TRANSFER", "CASH", "OTHER"];
const PURPOSES = ["NEW_SUBSCRIPTION", "RENEWAL", "PLAN_CHANGE", "OTHER"];

const RecordPaymentModal = ({ isOpen, onClose, onSaved, schoolId, hasActiveSubscription }) => {
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState("");
  const [purpose, setPurpose] = useState(hasActiveSubscription ? "RENEWAL" : "NEW_SUBSCRIPTION");
  const [paymentMethod, setPaymentMethod] = useState("CASH");
  const [manualReference, setManualReference] = useState("");
  const [manualNote, setManualNote] = useState("");
  const [idempotencyKey, setIdempotencyKey] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setIdempotencyKey(crypto.randomUUID());
    setPurpose(hasActiveSubscription ? "RENEWAL" : "NEW_SUBSCRIPTION");
    fetchPlans({ activeOnly: true })
      .then((res) => setPlans(res.data || []))
      .catch(() => setPlans([]));
  }, [isOpen, hasActiveSubscription]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!planId) {
      toast.error("Select a plan");
      return;
    }
    setSubmitting(true);
    try {
      await recordManualPayment(schoolId, {
        planId,
        purpose,
        paymentMethod,
        manualReference: manualReference.trim() || undefined,
        manualNote: manualNote.trim() || undefined,
        idempotencyKey,
      });
      toast.success("Payment recorded");
      onSaved?.();
      onClose();
    } catch (error) {
      toast.error(extractApiErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Record Payment</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Plan *</Label>
            <Select value={planId} onValueChange={setPlanId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a plan" />
              </SelectTrigger>
              <SelectContent>
                {plans.map((plan) => (
                  <SelectItem key={plan._id} value={plan._id}>
                    {plan.name} — {formatPaise(plan.baseAmount)} / {plan.durationMonths}mo / {plan.maxUsers} users
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Purpose *</Label>
              <Select value={purpose} onValueChange={setPurpose}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PURPOSES.map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Payment Method *</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Reference (optional)</Label>
            <Input value={manualReference} onChange={(e) => setManualReference(e.target.value)} placeholder="e.g. UTR / cheque no." />
          </div>
          <div>
            <Label>Note (optional)</Label>
            <Input value={manualNote} onChange={(e) => setManualNote(e.target.value)} placeholder="Optional note" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Recording..." : "Record Payment"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RecordPaymentModal;
